import React, {forwardRef, useEffect, useImperativeHandle, useRef} from "react"
import {Space} from "antd";
import {toast, ToastContainer} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {useDispatch, useSelector} from "react-redux";
import dayjs from "dayjs";
import {findEquidistantPointsAlongRoute, splitRoute} from "../util/gis";
import * as echarts from 'echarts';
import $ from 'jquery';
import {setTrajectoryStore} from "../store/entities/trajectory";
import {findClosestIndex, scale} from "../util/numbers";
import {getDatesBetween} from "../util/dates";
import {useDebouncedEffect} from "./util/UseDebouncedEffect";
import {v4 as uuidv4} from 'uuid';
import length from '@turf/length';
import distance from '@turf/distance';
import * as turf from '@turf/helpers'
import * as polyline from 'polyline'
import * as wellknown from 'wellknown'
import * as Papa from 'papaparse';
import {useHotkeys} from "react-hotkeys-hook";
import {totallyDarkeChartsTheme} from "../util/theme";
import download from "../util/download";

import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import {StatisticsModal} from "./StatisticsModal";

[utc, duration, timezone].map(dayjs.extend);

const MAX_ROUTE_LENGTH = 55;
const MAX_DATE_RANGE = 7;

const FIRST_BUCKET_SIZE = 15;
const BUCKET_SIZE = 10;
const MAX_SPEED = 65;

const MAX_CONCURRENT_REQUESTS = 10;
const MAX_REQUEST_ROUTE_LENGTH = 10;
const MAX_REQUEST_RETRIES = 3;
const useDarkMode = (localStorage.getItem('darkMode') === 'true')

/*
TL;DR: Pretty much all of these should be useRef, but I don't have the time or patience to mess with it. React sucks.
Expanded:
These variables exist because all event listeners are put into closures with the current state values.
When they are called, they use the old state values. This ruins the whole point of React and I don't understand why
people fan-boy for it so much. It is a terrible framework, since by nature it sort of forces you to do jank stuff.
The whole concept of useRef eliminates the whole purpose of React in the first place,
which is to, in my opinion, make keeping track of state and side effects easy. All it does is make it
incredibly difficult. I feel like I spent 10x as long debugging issues that wouldn't even exist in vanilla JS.
Haven't tried Angular/Vue or the other big frameworks, but I've heard they're much, much better.
Sorry for this rant, it had to go somewhere.

And all Redux does is make it so developers can be lazy by just making global variables, but a more complicated way that
looks good on their resume. Don't even get me started...
 */
let chart;
let wejoChartOptions;
let maxMileMarker = 0;
let lastHoverMarkerUpdateTime = performance.now();
let equidistantGeoJSON;
let timeZoneName;
let timeZoneId;
let slideshowInterval;

let curRequestIds = [];
let numCompletedRequests = 0;
let numTotalRequests = 0;

// The variables hold the series' for eCharts
let speedSeriesArray;
let hardBrakingHistogramSeries;
let hardAccelerationHistogramSeries;
let hardBrakingSeries;
let hardAccelerationSeries;
let travelTimeSeries;
let travelTimeMASeries;
let freeFlowTravelTimeSeries;
let interchangeSeries;
// let travelTimeJourneyIds = [];
let travelTimeJourneys = {};
// These variables hold the raw data that we get from the backend
let movementData = [];
let eventData = [];
let truckData = [];
let travelTimeData = [];
let travelTimeMAData = [];
let travelTimePointSize = 3;
let milesShown = 10000;
let hoveredTrajectoryClicked = false;
let hoverMarkerPositionLocal = null;
let startDateLocal, endDateLocal = null;
let routeNameTemp = null;

const TrajectoryChart = forwardRef((props, ref) => {
      const distanceBetweenPoints = 0.01;

      const dispatch = useDispatch();

      // Grab stuff from the store
      const startDateStore = useSelector(state => state.entities.trajectory.startDate);
      const endDateStore = useSelector(state => state.entities.trajectory.endDate);
      const roadWidthStore = useSelector(state => state.entities.trajectory.roadWidth);
      const mileMarkerOffsetStore = useSelector(state => state.entities.trajectory.mileMarkerOffset);
      const subtractMileMarkersStore = useSelector(state => state.entities.trajectory.subtractMileMarkers);
      const flipMileMarkersStore = useSelector(state => state.entities.trajectory.flipMileMarkers);
      const includeTrucksStore = useSelector(state => state.entities.trajectory.includeTrucks);
      const routeStore = useSelector(state => state.entities.trajectory.route);
      const sessionIdStore = useSelector(state => state.entities.trajectory.sessionId);
      const routeNameStore = useSelector(state => state.entities.trajectory.routeName);
      const movementTableStore = useSelector(state => state.entities.trajectory.movementTable);
      const truckTableStore = useSelector(state => state.entities.trajectory.truckTable);
      const eventTableStore = useSelector(state => state.entities.trajectory.eventTable);
      const maxTravelTimeStore = useSelector(state => state.entities.trajectory.maxTravelTime);
      const showSplitLinesStore = useSelector(state => state.entities.trajectory.showSplitLines);
      const freeFlowTravelTimeStore = useSelector(state => state.entities.trajectory.freeFlowTravelTime);
      const isLiveModeEnabledStore = useSelector(state => state.entities.trajectory.isLiveModeEnabled);
      const maxDistanceFromEndsStore = useSelector(state => state.entities.trajectory.maxDistanceFromEnds);
      const accelThresholdStore  = useSelector(state => state.entities.trajectory.accelThreshold);
      const organizationNameStore = useSelector(state => state.entities.trajectory.organizationName);

      // Component state (local stuff)
      const onlyTravelTimes = useRef([]);
      const toastId = useRef();
      const liveRefreshInterval = useRef();
      const maxUnixTime = useRef();
      const movementTableRef = useRef(movementTableStore);
      const truckTableRef = useRef(truckTableStore);
      const eventTableRef = useRef(eventTableStore);
      const sessionIdRef = useRef();

      const showAccelerationChangeHistogramRef = useRef(!!eventTableStore)
      const showTravelTimePlotRef = useRef(true);
      // const showTruckDataRef = useRef(true);

      const mileMarkerOffsetRef = useRef(mileMarkerOffsetStore);
      const subtractMileMarkersRef = useRef(subtractMileMarkersStore);
      const flipMileMarkersRef = useRef(flipMileMarkersStore);
      const includeTrucksRef = useRef(includeTrucksStore);
      const maxTravelTimeRef = useRef(maxTravelTimeStore);
      const showSplitLinesRef = useRef(showSplitLinesStore)
      const freeFlowTravelTimeRef = useRef(freeFlowTravelTimeStore);
      const maxDistanceFromEndsRef = useRef(maxDistanceFromEndsStore);

      const BUCKET_LINE_STYLES = [{color: '#ea00ea'}, {color: '#d30202'}, {color: '#ef4309'}, {color: '#f9b731'},
        {color: '#efea5b'}, {color: '#ccff99'}, {color: '#7fea33'}, {color: 'rgba(0,11,172,0.46)'}];

      useHotkeys('[', () => {
        if (travelTimePointSize > 3) {
          travelTimePointSize--;
          updateTravelTimePointSize();
        }
      })
      useHotkeys(']', () => {
        if (travelTimePointSize < 10) {
          travelTimePointSize++;
          updateTravelTimePointSize();
        }
      })
      useHotkeys('d', () => {
        if (Object.keys(travelTimeJourneys).length) downloadTrips();
        else toast.error("No trips yet, please wait for them to load.");
      })
      useHotkeys('h', () => {
        showAccelerationChangeHistogramRef.current = !showAccelerationChangeHistogramRef.current;
      })
      useHotkeys('t', () => showTravelTimePlotRef.current = !showTravelTimePlotRef.current)
      // useHotkeys('t d', () => {
      //   showTruckDataRef.current = !showTruckDataRef.current;
      //   chart.setOption({series: []}, {notMerge: false})
      //   wejoChartOptions = getChartOptions(startDateTime, endDateTime);
      //   updateChartOptions();
      //   addMovementDataToPlot();
      //   addEventDataToPlot();
      //   chart.setOption(wejoChartOptions, {notMerge: false})
      //
      // })
      useHotkeys('p', () => {
        if (curRequestIds.find(r => r.url.match('journeys')) || travelTimeData.length === 0) {
          toast.error("Not all trips have loaded, please wait.");
          return;
        }

        if (chart) {
          let i = 0;
          let slideshowSpeed = parseInt(localStorage.getItem('slideshowSpeed'));
          let slideshowSaveScreenshots = localStorage.getItem('slideshowSaveScreenshots') === 'true';
          if (slideshowInterval) {
            clearInterval(slideshowInterval);
            slideshowInterval = null;
            dispatch(setTrajectoryStore({slideshowRunning: false}))
            toast.warn(`Ended slideshow`);
          } else {
            toast.info(`Starting slideshow${(slideshowSaveScreenshots) ? ' and saving screenshots' : ''}`, {
              autoClose: (slideshowSaveScreenshots) ? (slideshowSpeed - 400) : 2000,
              closeOnClick: true,
              pauseOnFocusLoss: false,
              pauseOnHover: false,
            });
            dispatch(setTrajectoryStore({slideshowRunning: true}))
            slideshowInterval = setInterval(() => {
              // The series index may very well change
              let action = {
                type: 'select',
                seriesIndex: 0,
                name: travelTimeData[i][3]
              }
              if (i >= travelTimeData.length - 1) {
                i = 0;
                // Don't loop if we're saving screenshots
                if (slideshowSaveScreenshots) {
                  clearInterval(slideshowInterval);
                  slideshowInterval = null;
                  dispatch(setTrajectoryStore({slideshowRunning: false}))
                }
              } else i++;
              chart.dispatchAction(action)
            }, slideshowSpeed)
          }
        }
      });

      useEffect(() => mileMarkerOffsetRef.current = mileMarkerOffsetStore, [mileMarkerOffsetStore]);
      useEffect(() => flipMileMarkersRef.current = flipMileMarkersStore, [flipMileMarkersStore]);
      useEffect(() => subtractMileMarkersRef.current = subtractMileMarkersStore, [subtractMileMarkersStore]);
      useEffect(() => includeTrucksRef.current = includeTrucksStore, [includeTrucksStore]);
      useEffect(() => maxTravelTimeRef.current = maxTravelTimeStore, [maxTravelTimeStore]);
      useEffect(() => movementTableRef.current = movementTableStore, [movementTableStore]);
      useEffect(() => truckTableRef.current = truckTableStore, [truckTableStore]);
      useEffect(() => eventTableRef.current = eventTableStore, [eventTableStore]);
      useEffect(() => showSplitLinesRef.current = showSplitLinesStore, [showSplitLinesStore]);
      useEffect(() => freeFlowTravelTimeRef.current = freeFlowTravelTimeStore, [freeFlowTravelTimeStore]);
      useEffect(() => maxDistanceFromEndsRef.current = maxDistanceFromEndsStore, [maxDistanceFromEndsStore]);
      useEffect(() => sessionIdRef.current = sessionIdStore, [sessionIdStore]);
      useEffect(() => routeNameTemp = routeNameStore, [routeNameStore]);

      useEffect(() => {
        startDateLocal = startDateStore;
        endDateLocal = endDateStore;
      }, [startDateStore, endDateStore])

      useEffect(() => {
        const [startDateTime, endDateTime] = getDateTimes([startDateStore, endDateStore], timeZoneId)

        if (chart && wejoChartOptions) {
          chart.setOption({series: []}, {notMerge: false})
          wejoChartOptions = getChartOptions(startDateTime, endDateTime);
          updateChartOptions();
          addEventDataToPlot();
          chart.setOption(wejoChartOptions, {notMerge: false})
        }
      }, [showAccelerationChangeHistogramRef.current, showTravelTimePlotRef.current])

      const updateTravelTimePointSize = () => {
        if (chart) {
          travelTimeSeries.symbolSize = travelTimePointSize;
          chart.setOption({series: getAllSeries(false)});
        }
      }

      useEffect(() => {
        localStorage.setItem('showSplitLines', showSplitLinesStore);
        if (wejoChartOptions) {
          wejoChartOptions.xAxis[1].splitLine.show = showSplitLinesStore;
          wejoChartOptions.xAxis[1].minorSplitLine.show = showSplitLinesStore;
          chart.setOption({xAxis: wejoChartOptions.xAxis});
        }
      }, [showSplitLinesStore])

      // Listens for mile marker adjustment stuff to change
      useDebouncedEffect(() => {
        addEventDataToPlot();
        if (wejoChartOptions) updateChartOptions();
        if (chart) {
          chart.setOption({
            yAxis: wejoChartOptions.yAxis,
            xAxis: wejoChartOptions.xAxis,
          }, {
            replaceMerge: ['xAxis', 'yAxis'],
          })
        }
      }, [mileMarkerOffsetStore, subtractMileMarkersStore, flipMileMarkersStore], 250)

      useDebouncedEffect(() => {
        let [startDateTime, endDateTime] = getDateTimes([startDateStore, endDateStore], timeZoneId)
        calculateTravelTimes(startDateTime, endDateTime);
        toast.info("Refreshing travel time trips, please wait...");
      }, [maxDistanceFromEndsStore], 1000);

      useEffect(() => {
        // if (loadedMaxTravelTime) {
        // setMaxTravelTime(maxTravelTimeStore);
        let max = 0;
        if (!maxTravelTimeStore) max = Math.max(...onlyTravelTimes.current);
        else max = maxTravelTimeStore
        updateMaxTravelTimeOnPlot(max)
        // }
      }, [maxTravelTimeStore, onlyTravelTimes.current])

      const updateMaxTravelTimeOnPlot = travelTime => {
        if (chart) {
          wejoChartOptions.yAxis[2].max = travelTime;
          chart.setOption({yAxis: wejoChartOptions.yAxis})
        }
      }

      useEffect(() => showAccelerationChangeHistogramRef.current = !!eventTableStore, [eventTableStore])

      useEffect(() => {
        const isTodaySelected = dayjs(endDateStore).endOf('day').isAfter(dayjs());
        if (isLiveModeEnabledStore && isTodaySelected && !liveRefreshInterval.current) {
          liveRefreshInterval.current = setInterval(() => {
            if (movementData && maxUnixTime.current) {
              createRequestForWejoData({
                'route': equidistantGeoJSON,
                'startDateTime': dayjs.unix(maxUnixTime.current).toISOString(),
                'endDateTime': dayjs().toISOString(),
                'roadWidth': roadWidthStore,
                // 'simpleRoute': route,
                movementTable: movementTableRef.current
              }, 'get_wejo_points', processNewMovementData)
            }
          }, 15 * 60 * 1000)
        }
        if ((!isLiveModeEnabledStore || !isTodaySelected) && liveRefreshInterval.current) {
          clearInterval(liveRefreshInterval.current);
          liveRefreshInterval.current = null;
        }
      }, [isLiveModeEnabledStore, endDateStore])

      useImperativeHandle(ref, () => ({
        createPlot() {
          if (!routeStore) {
            toast.error(`No route selected!`);
            return;
          }

          // Reset the data!
          movementData = [];
          eventData = [];
          travelTimeData = [];
          dispatch(setTrajectoryStore({hoveredTrajectoryJourneyId: null, hoverMarkerPosition: null, travelTimes: []}))
          hoverMarkerPositionLocal = null;
          curRequestIds = [];
          travelTimeJourneys = {};
          numTotalRequests = 0;
          numCompletedRequests = 0;

          let oneDay = dayjs.duration({days: 1}).subtract({seconds: 1});
          let tempRoute = JSON.parse(JSON.stringify(routeStore));
          // for (let p of tempRoute.coordinates) {
          //     p.reverse();
          // }
          equidistantGeoJSON = findEquidistantPointsAlongRoute(tempRoute, distanceBetweenPoints)

          let timezoneUrl = new URL("https://maps.googleapis.com/maps/api/timezone/json");
          timezoneUrl.searchParams.append('location', tempRoute.coordinates[0].slice().reverse());
          timezoneUrl.searchParams.append('timestamp', dayjs.utc(startDateStore).unix());
          timezoneUrl.searchParams.append('key', 'AIzaSyDGn3bBeYtu5wHIAzLvfPDwjTNodmEAv-Q');

          $.get(timezoneUrl.href, (data) => {
            timeZoneId = data.timeZoneId;
            timeZoneName = data.timeZoneName;
            const [startDateTime, endDateTime] = getDateTimes([startDateStore, endDateStore], timeZoneId)


            // showRoutePolyline(routeGeoJSON.coordinates);
            // originalBounds = createBounds(equidistantGeoJSON.coordinates)
            // this.map.fitBounds(originalBounds);

            // Find the maximum mile marker
            maxMileMarker = Math.max(...equidistantGeoJSON.mileMarkers)
            milesShown = maxMileMarker;

            if (maxMileMarker > MAX_ROUTE_LENGTH) {
              toast.error(`Route too long, max ${MAX_ROUTE_LENGTH} miles!`);
              return;
            }
            let totalDays = Math.abs(dayjs(startDateTime).diff(dayjs(endDateTime), 'days'));
            if (totalDays > MAX_DATE_RANGE) {
              toast.warning(`Too many days selected, ${MAX_DATE_RANGE} max.`);
              return;
            }

            // Get rid of the old chart if it still exists
            if (chart) {
              chart.dispose();
            }
            $("#trajectory-chart").css('padding-top', '0');
            // echarts.registerLayout(myCustomLayoutFunction)
            echarts.registerTheme('totally-dark-theme', totallyDarkeChartsTheme);
            chart = echarts.init(document.getElementById('trajectory-chart'), (useDarkMode) ? 'totally-dark-theme' : 'light', {
              renderer: 'canvas',
              locale: 'EN'
            });

            speedSeriesArray = createSpeedSeriesArray(FIRST_BUCKET_SIZE, BUCKET_SIZE, MAX_SPEED);
            travelTimeSeries = createTravelTimeSeries(true);
            freeFlowTravelTimeSeries = createFreeFlowTravelTimeSeries();
            interchangeSeries = createInterchangeSeries();
            travelTimeMASeries = createTravelTimeMASeries();
            if (eventTableStore) {
              hardBrakingHistogramSeries = createAccelerationChangeHistogramSeries('BRAKE');
              hardAccelerationHistogramSeries = createAccelerationChangeHistogramSeries('ACCELERATION');
              hardBrakingSeries = createAccelerationChangeEventSeries('BRAKE');
              hardAccelerationSeries = createAccelerationChangeEventSeries('ACCELERATION');
            }

            wejoChartOptions = getChartOptions(startDateTime, endDateTime);
            updateChartOptions();

            chart.on('datazoom', dataZoomListener)

            chart.on('globalout', () => {
              dispatch(setTrajectoryStore({hoverMarkerPosition: null}));
              hoverMarkerPositionLocal = null;
            })

            chart.on('legendselectchanged', params => {
              if (params.selected['Hard Acceleration']) {
                if (chart) {
                  chart.setOption({series: getAllSeries()})
                }
              }
            })

            chart.on('mouseout', params => {
              // TODO Find a better way to determine the series that is being mouseout-ed
              if (params.seriesType === 'scatter') {
                if (!hoveredTrajectoryClicked) dispatch(setTrajectoryStore({hoveredTrajectoryJourneyId: null}));
              }
            })

            chart.on('selectChanged', params => {
              if (params.fromAction === 'select' && params.selected.length) {
                let selectedPoint = wejoChartOptions.series[params.selected[0].seriesIndex].data[params.selected[0].dataIndex[0]]
                if (selectedPoint) {
                  dispatch(setTrajectoryStore({
                    hoveredTrajectoryJourneyId: travelTimeJourneys[selectedPoint.name],
                  }));
                  hoveredTrajectoryClicked = selectedPoint.name;
                }
              } else {
                // dispatch(setTrajectoryStore({
                //     hoveredTrajectoryCoordinates: travelTimeJourneys[selectedPoint],
                // }));
                hoveredTrajectoryClicked = null;
              }
              //
              // if (params.data.length === 4) {
              //     if ((params.data[3] in travelTimeJourneys)) {
              //         dispatch(setTrajectoryStore({
              //             hoveredTrajectoryCoordinates: travelTimeJourneys[params.data[3]],
              //         }));
              //         hoveredTrajectoryClicked = params.data[3];
              //     }
              // }
            })

            let chartEl = $('#trajectory-chart');
            if (chartEl) {
              chartEl[0].addEventListener('contextmenu', e => {
                if (hoverMarkerPositionLocal) {
                  let idx = findClosestIndex(hoverMarkerPositionLocal, equidistantGeoJSON.mileMarkers)
                  let coordinate = equidistantGeoJSON.coordinates[idx];
                  // let latLng = {lng: coordinate[0], lat: coordinate[1]}
                  window.open(`https://maps.google.com/?q=${coordinate[1]},${coordinate[0]}`)
                }
              })
            }

            $(window).resize(() => {
              setTimeout(() => {
                chart.setOption({series: []}, {notMerge: false})
                chart.setOption(wejoChartOptions, {notMerge: false})
                chart.resize();
              }, 200);
            })

            chart.showLoading('default', {
              text: 'Gathering data... This may take a bit.',
              fontSize: 20,
              textColor: (useDarkMode) ? '#fff' : '#000',
              maskColor: (useDarkMode) ? 'rgba(0,0,0,0.8)' : 'rgba(255, 255, 255, 0.8)',
              fontWeight: 'bold',
              spinnerRadius: 25,
              lineWidth: 5,
            })

            // use configuration item and data specified to show chart
            chart.setOption(wejoChartOptions);

            dispatch(setTrajectoryStore({
              totalTrajectories: 0,
              totalPoints: 0,
              totalHardBrakingEvents: 0,
              totalHardAccelerationEvents: 0,
              totalTravelTimePoints: 0
            }));
            // setTotalTrajectories(0);
            // setTotalPoints(0);
            // setTotalHardBrakingEvents(0);
            // setTotalTravelTimePoints(0);


            createRequestForInterchanges(equidistantGeoJSON)

            // Create request for event data
            if (eventTableStore) {
              console.log('AccelThreshold is: ', accelThresholdStore);
              createRequestForWejoData({
                'route': equidistantGeoJSON,
                'startDateTime': dayjs(startDateTime).toISOString(),
                'endDateTime': dayjs(endDateTime).toISOString(),
                'roadWidth': roadWidthStore,
                'accelThreshold': accelThresholdStore,
                // 'simpleRoute': route,
                eventTable: eventTableStore
              }, 'get_wejo_events', processNewEventData)
            }

            // Create request for points
            let days = getDatesBetween(dayjs(startDateTime), dayjs(endDateTime))
            let routes = splitRoute(equidistantGeoJSON, MAX_REQUEST_ROUTE_LENGTH);
            numTotalRequests = (days.length * routes.length) + eventTableStore ? 1 : 0;

            toastId.current = toast.info(`Rendering, please wait!`, {
              progress: numCompletedRequests / numTotalRequests,
              autoClose: false
            });

            if (days) {
              for (let i = 0; i < days.length; i++) {
                for (let splitRoute of routes) {
                  createRequestForWejoData({
                    'route': splitRoute,
                    'startDateTime': days[i][0],
                    'endDateTime': days[i][1],
                    'roadWidth': roadWidthStore,
                    // 'simpleRoute': route,
                    movementTable: movementTableStore,
                  }, 'get_wejo_points', processNewMovementData)

                  if (includeTrucksRef.current && truckTableStore) {
                    createRequestForWejoData({
                      'route': splitRoute,
                      'startDateTime': days[i][0],
                      'endDateTime': days[i][1],
                      'roadWidth': roadWidthStore,
                      // 'simpleRoute': route,
                      'truckTable': truckTableStore,
                    }, 'get_wejo_truck_points', processNewTruckData)
                  }
                }
              }
            }
          });
        }
      }));

      const getChartOptions = (startDateTime, endDateTime) => {
        let option = {
          dataset: [],
          grid: createGrids(),
          tooltip: {
            trigger: 'item',
            axisPointer: {
              type: 'cross',
              animation: false,
              label: {
                backgroundColor: useDarkMode ? 'rgba(227,227,227,0.8)' : 'rgba(28,28,28,0.8)',
                borderColor: '#aaa',
                borderWidth: 1,
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowOffsetY: 0,
                color: (useDarkMode) ? '#000' : '#fff'
              }
            },
            // formatter: function (params) {
            //     return params[2].name + '<br />' + ((params[2].value - base) * 100).toFixed(1) + '%';
            // }
          },
          toolbox: {
            feature: {
              saveAsImage: {
                name: `Trajectory Heatmap ${startDateStore}_${endDateStore}`,
                pixelRatio: 8
              },
              dataZoom: {
                brushStyle: {
                  borderColor: (useDarkMode) ? '#fff' : '#000',
                  borderWidth: 0.5,
                  opacity: 0.75,
                }
              },
            }
          },
          xAxis: createXAxis(startDateTime, endDateTime),
          yAxis: createYAxis(),
          series: getAllSeries(),
          legend: {
            show: true,
            type: 'scroll',
            width: '75%',
            selected: {
              'Hard Accel': false,
              'Hard Accel Histogram': false,
              'Interchanges': false
            }
          },
          dataZoom: [
            {
              yAxisIndex: [1, 0],
              type: 'slider',
              show: true,
              realtime: false,
              filterMode: 'empty',
              showDataShadow: false,
              labelFormatter: d => {
                return d.toFixed(2);
              }
            },
            {
              xAxisIndex: [1, 2],
              type: 'slider',
              show: true,
              realtime: true,
              filterMode: 'filter',
              showDataShadow: false,
              throttle: 10,
              labelFormatter: d => {
                return (dayjs(d).tz(timeZoneId).format("ddd M/DD HH:mm"))
              },
            }
          ],
        }
        return option;
      }

      const createGrids = () => {
        if (showTravelTimePlotRef.current && showAccelerationChangeHistogramRef.current) {
          return [
            {
              // Acceleration change histogram
              show: false,
              width: '100',
              // right: '90%',
              left: '10',
              bottom: '275',
              top: '40'
            },
            {
              // Main plot
              top: '40',
              right: '80',
              bottom: '275',
              left: '200'
            },
            {
              // Travel time scatter plot
              show: false,
              height: '150',
              bottom: '50',
              right: '80',
              left: '200'
            }
          ]
        } else if (showAccelerationChangeHistogramRef.current && !showTravelTimePlotRef.current) {
          return [
            {
              // Acceleration change histogram
              show: false,
              width: '100',
              // right: '90%',
              left: '10',
              bottom: '100',
              top: '40'
            },
            {
              // Main plot
              top: '40',
              right: '80',
              bottom: '100',
              left: '200'
            },
            {
              // Travel time scatter plot
              show: false,
              height: '0',
              width: '0',
            }
          ]
        } else if (!showAccelerationChangeHistogramRef.current && showTravelTimePlotRef.current) {
          return [
            {
              // Acceleration change histogram
              show: false,
              width: '0',
              height: '0',
            },
            {
              // Main plot
              top: '40',
              right: '80',
              bottom: '275',
              left: '100'
            },
            {
              // Travel time scatter plot
              show: false,
              height: '150',
              bottom: '50',
              right: '80',
              left: '100'
            }
          ]
        } else if (!showAccelerationChangeHistogramRef.current && !showTravelTimePlotRef.current) {
          return [
            {
              // Acceleration change histogram
              show: false,
              width: '0',
              height: '0',
            },
            {
              // Main plot
              top: '40',
              right: '80',
              bottom: '100',
              left: '100'
            },
            {
              // Travel time scatter plot
              show: false,
              height: '0',
              width: '0'
            }
          ]
        }
      }

      const createXAxis = (startDateTime, endDateTime) => {
        let accelerationChangeHistogramAxis = {
          gridIndex: 0,
          inverse: true,
          axisTick: {show: false},
          // max: 5,
          name: ['JTRP-New','INDOT-New','CalTrans-New','CTDOT-New','GDOT-New','MnDOT-New','MSDOT-New','NCDOT-New','ODOT-New','PennDOT-New','TxDOT-New','UDOT-New','WisDOT-New'].includes(organizationNameStore)
          ? `# Hard Brake/Accel\n( |Accel| ≥ ${accelThresholdStore} g )`
          : "# Hard Brake/Accel",
          nameLocation: 'center',
          nameGap: 30,
          axisPointer: {
            label: {
              fontSize: 16,
              formatter: (d) => {
                // Get rid of the marker
                return Math.floor(d.value)
              },
            },
          }
        }
        let dateAxis = {
          // min: dayjs.utc(this.startDateTime).unix(),
          // max: dayjs.utc(this.endDateTime).add(1, 'second').unix(),
          min: startDateTime,
          max: dayjs(endDateTime).add(1, 'second').toISOString(),
          gridIndex: 1,
          type: "time",
          show: true,
          showGrid: true,
          // splitLine: {show: false},
          name: (timeZoneName) ? "Date (" + timeZoneName + ")" : "Date",
          nameLocation: 'center',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 18
          },
          splitLine: {
            show: showSplitLinesRef.current,
            lineStyle: {
              color: (useDarkMode) ? '#fff' : '#000',
              width: 1.5,
              type: 'dotted',
            }
          },
          minorSplitLine: {
            show: showSplitLinesRef.current,
            lineStyle: {
              color: (useDarkMode) ? '#fff' : '#000',
              width: 1,
              type: 'dotted'
            }
          },
          minorTick: {
            show: true,
            splitNumber: 4
          },
          axisLabel: {
            show: true,
            formatter: tstamp => {
              let d = dayjs(new Date(tstamp)).tz(timeZoneId);
              if (!d.hour() && !d.minute()) return d.format('ddd M/D')
              else return d.format('HH:mm')
            },
            fontSize: 16,
            showMinLabel: true,
            showMaxLabel: true,
          },
          axisTick: {},
          axisPointer: {
            label: {
              formatter: tstamp => {
                let d = dayjs(new Date(tstamp.value)).tz(timeZoneId);
                return d.format('YYYY-MM-DD HH:mm:ss')
              }
            },
            lineStyle: {
              // color: '#000',
              color: (useDarkMode) ? '#fff' : '#000',
              width: 1,
              type: 'dashed'
            },
            // z: 100000
          }
        }
        let travelTimeAxis = {
          min: startDateTime,
          max: dayjs(endDateTime).add(1, 'second').toISOString(),
          type: 'time',
          gridIndex: 2,
          axisLabel: {show: false},
          axisTick: {show: false},
          splitLine: {show: false},
          axisPointer: {
            label: {
              fontSize: 16,
              formatter: tstamp => {
                let d = dayjs(new Date(tstamp.value)).tz(timeZoneId);
                return d.format('YYYY-MM-DD HH:mm:ss')
              }
            },
            lineStyle: {
              // color: '#000',
              color: (useDarkMode) ? '#fff' : '#000',
              width: 1,
              type: 'dashed'
            },
            // z: 100000
          }
        };

        if (!showAccelerationChangeHistogramRef.current) {
          accelerationChangeHistogramAxis.axisTick = {show: false};
          accelerationChangeHistogramAxis.axisLabel = {show: false};
          accelerationChangeHistogramAxis.name = '';
        }
        return [accelerationChangeHistogramAxis, dateAxis, travelTimeAxis];
        // return
      }

      const createYAxis = () => {
        let accelerationChangeHistogramAxis = {
          inverse: subtractMileMarkersRef.current ^ flipMileMarkersRef.current,
          gridIndex: 0,
          type: 'category',
          axisLabel: {show: false},
          axisTick: {show: false},
          splitLine: {show: false},
          axisPointer: {
            label: {
              fontSize: 16,
              formatter: (d) => {
                // Get rid of the marker
                // TODO Remove pointer marker!!
                dispatch(setTrajectoryStore({
                  hoverMarkerPosition: null
                }));
                hoverMarkerPositionLocal = null;
                // removePointerMarker();
                return d.value
              },
            },
          }
        };
        let distanceAxis = {
          // min: (subtractMileMarkers) ? mileMarkerOffset - maxMileMarker : mileMarkerOffset,
          // max: (subtractMileMarkers) ? mileMarkerOffset : maxMileMarker + mileMarkerOffset,
          min: 0,
          max: maxMileMarker,
          gridIndex: 1,
          type: "value",
          show: true,
          showGrid: true,
          name: "Distance From Start/Mile Marker",
          nameLocation: 'center',
          nameGap: 55,
          nameTextStyle: {
            fontSize: 18
          },
          minInterval: .1,
          maxInterval: 2,
          axisLine: {show: true},
          splitLine: {show: false},
          axisTick: {show: true},
          axisLabel: {
            show: true,
            showMaxLabel: false,
            showMinLabel: false,
            // showMaxLabel: subtractMileMarkers,
            // showMinLabel: !subtractMileMarkers,
            fontSize: 16,
            formatter: d => {
              if (milesShown > 10) {
                return getRoundedMileMarker(d).toFixed(0)
              }
              return getRoundedMileMarker(d)
            }
          },
          axisPointer: {
            label: {
              fontSize: 16,
              formatter: (d) => {
                // Move the marker on the map
                // Only update at 60fps
                if (performance.now() - lastHoverMarkerUpdateTime > 1000 / 60) {
                  dispatch(setTrajectoryStore({
                    hoverMarkerPosition: d.value
                  }));
                  hoverMarkerPositionLocal = d.value;
                  lastHoverMarkerUpdateTime = performance.now();
                }
                return getCorrectedMileMarker(d.value).toFixed(2);
              },
            },
            lineStyle: {
              // color: '#000',
              color: (useDarkMode) ? '#fff' : '#000',
              width: 1,
              type: 'dashed'
            },
            // z: 100000
          },
          inverse: subtractMileMarkersRef.current ^ flipMileMarkersRef.current
        };
        let travelTimeAxis = {
          gridIndex: 2,
          type: 'value',
          name: "Travel Time (minutes)",
          nameLocation: 'middle',
          nameGap: 40,
          min: 0,
          ...(maxTravelTimeRef.current && {max: maxTravelTimeRef.current}),
          axisLabel: {
            formatter: d => {
              return d.toFixed(1);
            },
            showMaxLabel: (!!maxTravelTimeRef.current)
          },
        }
        if (!showTravelTimePlotRef.current) {
          travelTimeAxis.name = '';
          travelTimeAxis.axisLabel.show = false;
        }
        return [accelerationChangeHistogramAxis, distanceAxis, travelTimeAxis];
      }

      const updateChartOptions = () => {
        // ^ = XOR
        wejoChartOptions.yAxis[0].inverse = subtractMileMarkersRef.current ^ flipMileMarkersRef.current;
        wejoChartOptions.yAxis[1].inverse = subtractMileMarkersRef.current ^ flipMileMarkersRef.current;
        // wejoChartOptions.yAxis[1].min = (subtractMileMarkers) ? mileMarkerOffset - maxMileMarker : mileMarkerOffset;
        // wejoChartOptions.yAxis[1].max = (subtractMileMarkers) ? mileMarkerOffset : maxMileMarker + mileMarkerOffset;
        wejoChartOptions.yAxis[1].axisLabel = {
          show: true,
          // showMaxLabel: subtractMileMarkersStore,
          // showMinLabel: !subtractMileMarkersStore,
          fontSize: 16,
          formatter: d => {
            if (milesShown > 10) {
              return getRoundedMileMarker(d).toFixed(0)
            }
            return getRoundedMileMarker(d)
          }
        }
      }

      Date.prototype.addDays = function (days) {
        let date = new Date(this.valueOf());
        date.setDate(date.getDate() + days);
        return date;
      }

      /**
       * Create an array of series' for each speed bucket
       * @param lineStyles The array of eCharts lineStyles to apply to the different series'
       * @param firstBucketSize The size of the first bucket (mph) (default 15 mph)
       * @param bucketSize The size of each bucket after the first (mph) (default 10 mph)
       * @param maxSpeed The max speed (mph) (default 65 mph)
       * @returns {[]} The array of series'
       */
      const createSpeedSeriesArray = (firstBucketSize = 15, bucketSize = 10, maxSpeed = 65, superSpeed = 85) => {
        let speedSeriesArray = []
        let numBuckets = ((maxSpeed - firstBucketSize) / bucketSize) + 2

        for (let i = 0; i < numBuckets; i++) {
          let seriesMinSpeed, seriesMaxSpeed, seriesName;
          // The first bucket
          if (i === 0) {
            seriesMinSpeed = 0;
            seriesMaxSpeed = firstBucketSize;
            seriesName = `${seriesMinSpeed}-${seriesMaxSpeed - 1} mph`
          }
          // The last bucket
          else if (i === numBuckets - 1) {
            seriesMinSpeed = maxSpeed;
            seriesMaxSpeed = Number.MAX_SAFE_INTEGER;
            seriesName = `${seriesMinSpeed}+ mph`
          }
          // Every bucket in between
          else {
            seriesMinSpeed = firstBucketSize + ((i - 1) * bucketSize);
            seriesMaxSpeed = firstBucketSize + (i * bucketSize);
            seriesName = `${seriesMinSpeed}-${seriesMaxSpeed - 1} mph`
          }
          speedSeriesArray.push({
            type: "lines",
            // symbol: 'none',
            name: seriesName,
            silent: true,
            animation: false,
            polyline: true,
            data: [],
            // clip: false,
            // largeThreshold: 1000000,
            large: true,
            // progressiveThreshold: Number.MAX_SAFE_INTEGER,
            // progressive: 0,
            // progressive: 0,
            // progressive: 200,
            // progressiveThreshold: 4000,
            coordinateSystem: 'cartesian2d',
            xAxisIndex: 1,
            yAxisIndex: 1,
            lineStyle: BUCKET_LINE_STYLES[i],
            z: (numBuckets - i) + 1000,
            minSpeed: seriesMinSpeed,
            maxSpeed: seriesMaxSpeed
          })
        }
        return speedSeriesArray;
      }

      /**
       * Given a speed, finds the appropriate speed series for it in a list of speed series'
       * @param speed The speed (mph)
       * @returns {Number}
       */
      const getSpeedSeriesIndex = (speed) => {
        let idx = null;
        for (let i = 0; i < speedSeriesArray.length; i++) {
          if (speed >= speedSeriesArray[i].minSpeed && speed < speedSeriesArray[i].maxSpeed) {
            idx = i;
            break;
          }
        }
        return idx;
      }

      /**
       * Creates a series for ACCELERATION_CHANGE events
       * @param value Should either be 'BRAKE' or 'ACCELERATION' for hard braking and hard acceleration, respectively
       * @returns {{symbol: string, silent: boolean, data: [], symbolSize: number, xAxisIndex: number, name: string, itemStyle: {borderColor: string, color: string, borderWidth: number}, z: number, coordinateSystem: string, type: string, yAxisIndex: number, animation: boolean}} The scatter series
       */
      const createAccelerationChangeEventSeries = value => {
        return {
          type: 'scatter',
          coordinateSystem: 'cartesian2d',
          xAxisIndex: 1,
          yAxisIndex: 1,
          name: "Hard " + ((value === 'BRAKE') ? "Braking" : "Accel"),
          data: [],
          symbol: 'diamond',
          symbolSize: 6,
          itemStyle: {
            color: (value === 'BRAKE') ? '#f00' : '#0f0',
            borderColor: (useDarkMode) ? '#fff' : '#000',
            borderWidth: 1,
          },
          // Events should have a z between 2000 and 3000
          z: 2000,
          animation: false,
          silent: true,
        };
      }

      const createAccelerationChangeHistogramSeries = value => {
        return {
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          barWidth: '95%',
          data: [],
          itemStyle: {
            color: (value === 'BRAKE') ? '#f00' : '#0f0',
          },
          animation: true,
          silent: true,
          name: "Hard " + (((value === 'BRAKE') ? "Braking" : "Accel") + " Histogram"),
          stack: 'total'
        };
      }

      const createTravelTimeSeries = () => {
        return {
          type: 'scatter',
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: [],
          symbolSize: travelTimePointSize,
          selectedMode: true,
          emphasis: {
            itemStyle: {
              color: '#d819fa',
              borderWidth: 0
            }

          },
          label: {
            show: false,
            color: '#d819fa',
            fontWeight: 'bold',
            fontSize: 16,
            formatter: d => d.value[1].toFixed(1)
          },
          select: {
            label: {
              show: true,
              color: '#d819fa',
              fontWeight: 'bold',
              fontSize: 16
            },
            // itemStyle: {
            //     color: '#d819fa',
            //     borderWidth: 0
            // }
          },
          itemStyle: {
            // color: '#000',
            color: d => {
              const isLoaded = travelTimeJourneys && Object.keys(travelTimeJourneys).includes(d.data.name);
              const isTruck = d.data.name.match(/^truck_/);
              if (isTruck) {
                if (isLoaded) return '#0063db';
                else return useDarkMode ? '#0F4B74' : '#80CCFF';
              } else {
                if (isLoaded) return useDarkMode ? '#FFFFFF' : '#000000';
                else return useDarkMode ? '#5F5F5F' : '#8E8E8E'
              }
              // if (isLoaded && isTruck) {
              // } else if (!isLoaded)
              // if (travelTimeJourneys && Object.keys(travelTimeJourneys).includes(d.data.name)) {
            }
          },
          animation: false,
          // silent: true,
          tooltip: {
            formatter: (data) => {
              let locationArr = [];
              let journeyId = data.data.name;
              if ((journeyId in travelTimeJourneys) && !hoveredTrajectoryClicked) {
                dispatch(setTrajectoryStore({hoveredTrajectoryJourneyId: travelTimeJourneys[journeyId]}));
              }
              // for (let point of movementData[data.data[3]]) {
              //     locationArr.push([point[4], point[5]])
              // }
              return null;
              // return data.data[3];
            }
          },
          seriesName: 'Travel Time'
        };
      }

      const createFreeFlowTravelTimeSeries = () => {
        return {
          type: 'line',
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: [{
            value: [[0, 0], [1, 1]],
            lineStyle: {opacity: 0},
          }],
          ...useDarkMode && {
            lineStyle: {color: '#0c30ec'},
            itemStyle: {color: '#0c30ec'}
          },
          markLine: {
            data: [{
              name: 'Google Maps Routing',
              label: {
                formatter: 'GMaps Travel Time',
                position: 'insideStartTop'
              },
              ...useDarkMode && {lineStyle: {color: '#0c30ec'}},
              yAxis: freeFlowTravelTimeRef.current / 60,
              animation: false
            }],
            symbol: ['none', 'none'],
            silent: true
          },
          animation: false,
          name: 'GMaps Travel Time'
        };
      }

      const createInterchangeSeries = () => {
        return {
          type: 'lines',
          xAxisIndex: 1,
          yAxisIndex: 1,
          polyline: true,
          coordinateSystem: 'cartesian2d',
          data: [],
          lineStyle: {
            ...useDarkMode && {lineStyle: {color: '#0c30ec'}},
            opacity: 1,
          },
          symbol: ['none', 'none'],
          silent: true,
          label: {
            show: false
          },
          z: 2000,
          animation: false,
          name: 'Interchanges'
        };
      }

      const createTravelTimeMASeries = () => {
        return {
          type: 'line',
          name: 'Travel Time Moving Average',
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: [],
          symbol: 'none',
          silent: true,
          animation: false,
          lineStyle: {
            width: 4,
            opacity: .75
          }
        }
      }

      /**
       * Returns an Array of all the relevant series that should be added to the plot
       * @returns {[]}
       */
      const getAllSeries = (progressive = true) => {
        if (progressive) {
          speedSeriesArray.forEach(s => {
            delete s.progressive;
            delete s.progressiveThreshold;
          })
        } else {
          speedSeriesArray.forEach(s => {
            s.progressive = 0;
            s.progressiveThreshold = Number.MAX_SAFE_INTEGER;
          })
        }

        return [
          // {
          //   ...travelTimeSeries,
          //   ...(showTruckDataRef.current && {data: travelTimeSeries.data.filter(d => !d.name.match(/^truck_/))})
          // },
          travelTimeSeries,
          ...(eventTableStore ? [hardBrakingSeries] : []),
          ...(eventTableStore ? [hardBrakingHistogramSeries] : []),
          ...(eventTableStore ? [hardAccelerationSeries] : []),
          ...(eventTableStore ? [hardAccelerationHistogramSeries] : []),
          ...(interchangeSeries.data.length ? [interchangeSeries] : []),
          freeFlowTravelTimeSeries,
          ...speedSeriesArray
        ];
      }

      /**
       * The function that should be set as the listener for the datazoom event
       * @param params Whatever eCharts passes
       */
      const dataZoomListener = (params) => {
        // This checks if it's the y-axis data zoom that was moved
        let start, end;
        if (params.batch) {
          for (let axis of params.batch) {
            if (axis.dataZoomId.includes('yAxis')) {
              start = axis.startValue;
              end = axis.endValue;

              let visibleCoordinates = equidistantGeoJSON.coordinates.slice(
                findClosestIndex(axis.startValue, equidistantGeoJSON.mileMarkers),
                findClosestIndex(axis.endValue, equidistantGeoJSON.mileMarkers));

              dispatch(setTrajectoryStore({
                visibleCoordinates: ((axis.startValue > .1 || axis.endValue < maxMileMarker - .1) ? visibleCoordinates : [])
              }));
              milesShown = maxMileMarker;
            }
          }
          chart.setOption({yAxis: createYAxis()})
          // For some ungodly reason, eCharts puts UTF-8 null characters in the dataZoomId string...
        } else if (params.dataZoomId && params.dataZoomId.replace(/\u0000/g, '') === 'series00') {
          start = params.start;
          end = params.end;

          let min, max;
          // if (!subtractMileMarkersRef.current) {
          min = scale(start, 0, 100, 0, maxMileMarker);
          max = scale(end, 0, 100, 0, maxMileMarker);
          // } else {
          //   min = scale(start, 0, 100, maxMileMarker, 0);
          //   max = scale(end, 0, 100, maxMileMarker, 0);
          // }
          let minIndex = findClosestIndex(min, equidistantGeoJSON.mileMarkers);
          let maxIndex = findClosestIndex(max, equidistantGeoJSON.mileMarkers);
          // if (subtractMileMarkersRef.current) {
          //   let tmp = minIndex;
          //   minIndex = maxIndex;
          //   maxIndex = tmp;
          // }
          milesShown = Math.abs(max - min);
          // maxIndex = (maxIndex > equidistantGeoJSON['coordinates'].length - 1) ? equidistantGeoJSON['coordinates'].length : maxIndex;

          let visibleCoordinates = equidistantGeoJSON.coordinates.slice(minIndex, maxIndex)

          dispatch(setTrajectoryStore({
            visibleCoordinates: (start > 1 || end < 99) ? visibleCoordinates : []
          }));
          chart.setOption({yAxis: createYAxis()})
        } else if (params.dataZoomId && params.dataZoomId.replace(/\u0000/g, '') === 'series10') {
          start = params.start;
          end = params.end;

          let [startDateTime, endDateTime] = getDateTimes([startDateStore, endDateStore], timeZoneId)

          let totalSeconds = endDateTime.diff(startDateTime, 'seconds')

          let min = scale(start, 0, 100, 0, totalSeconds);
          let max = scale(end, 0, 100, 0, totalSeconds);

          let selectedStart = startDateTime.clone().add(min, 'seconds')
          let selectedEnd = startDateTime.add(max, 'seconds')

          const millisPerMinute = 60 * 1000
          let roundMinutes;
          if (totalSeconds < 86400) roundMinutes = 15;
          else if (totalSeconds > 86400 && totalSeconds < 86400 * 3) roundMinutes = 30;
          else roundMinutes = 60;

          let startRounded = round(selectedStart, roundMinutes * millisPerMinute, "round")
          let endRounded = round(selectedEnd, roundMinutes * millisPerMinute, "round")

          if (chart) chart.dispatchAction({
            type: 'dataZoom',
            dataZoomIndex: 1,
            // start: 45,
            // end: 55
            startValue: startRounded.format(),
            endValue: endRounded.format()
          })
        }
      }

      const createRequestForInterchanges = () => {
        // Figure out the correct backend URL based on the window's current URL
        let url = '';
        if (window.location.href.match('trajectory-heatmap-dev-dot')) {
          url = 'https://trajectory-cruncher-dev-dot-tmc-dashboards.uc.r.appspot.com/interchange';
        } else if (window.location.href.match('trajectory-heatmap-dot')) {
          url = 'https://trajectory-cruncher-dot-tmc-dashboards.uc.r.appspot.com/interchange';
        } else {
          url = 'https://trajectory-cruncher-dev-dot-tmc-dashboards.uc.r.appspot.com/interchange';
        }

        let ajaxReq = {
          url,
          method: 'GET',
          beforeSend: xhr => xhr.setRequestHeader('Authorization', `Bearer ${sessionIdRef.current}`),
          success: rawCSV => {
            let data = Papa.parse(rawCSV, {header: true});
            data = data.data;
            for (let interchange of data) {
              if (!('point' in interchange)) continue;
              let closestIdx = 0;
              let closestDist = Number.MAX_SAFE_INTEGER;
              for (let i = 0; (i + 10) < equidistantGeoJSON.coordinates.length; i += 10) {
                let interchangeCoord = wellknown.parse(interchange.point);
                let p = turf.point(equidistantGeoJSON.coordinates[i])
                let dist = distance(interchangeCoord, turf.point(equidistantGeoJSON.coordinates[i]), {units: 'feet'})
                if (dist < closestDist) {
                  closestDist = dist;
                  closestIdx = i;
                }
              }
              if (closestDist > 500) continue;
              let mm = equidistantGeoJSON.mileMarkers[closestIdx];
              interchangeSeries.data.push({
                coords: [['1900-01-01 00:00', mm], ['9999-01-01 00:00', mm]]
              })
            }
            chart.setOption({series: getAllSeries()})
          },
          error: (xhr, textStatus, err) => {
            console.log({xhr, textStatus});
          }
        }
        $.ajax(ajaxReq)
      }

      const createRequestForWejoData = async (requestData, endpoint, addToPlotFunction) => {
        // Don't send requests if they are requesting data from the future
        let start = dayjs(requestData.startDateTime);
        if (dayjs().isBefore(start)) return;

        // Don't send request if they don't have a table specified, like get_wejo_journeys on live refresh
        if (!requestData.movementTable && !requestData.eventTable && !requestData.truckTable) return;

        // Don't send request for journeys if there are no journeySegments
        if (endpoint.match('journey') && !requestData.journeySegments.length) return;

        // Generate a little string to keep track of the request
        let requestId = uuidv4();

        // Figure out the correct backend URL based on the window's current URL
        let trajectoryCruncherUrl = '';
        if (window.location.href.match('trajectory-heatmap-dev-dot')) {
          trajectoryCruncherUrl = 'https://trajectory-cruncher-dev-dot-tmc-dashboards.uc.r.appspot.com/';
        } else if (window.location.href.match('trajectory-heatmap-dot')) {
          trajectoryCruncherUrl = 'https://trajectory-cruncher-dot-tmc-dashboards.uc.r.appspot.com/';
        } else {
          trajectoryCruncherUrl = 'https://trajectory-cruncher-dev-dot-tmc-dashboards.uc.r.appspot.com/';
        }
        let url = `${trajectoryCruncherUrl}${endpoint}`;

        let tryCount = 0
        let ajaxReq = {
          url,
          method: 'POST',
          beforeSend: xhr => xhr.setRequestHeader('Authorization', `Bearer ${sessionIdRef.current}`),
          data: JSON.stringify(requestData),
          requestId,
          contentType: 'application/json',
          dataType: 'json',
          sent: false,
          success: data => {
            data = data.data;
            // If it's not a current request, ignore it.
            if (!curRequestIds.find(req => req.requestId === requestId)) return;
            // Remove this request from the list
            curRequestIds = curRequestIds.filter(item => item.requestId !== requestId)
            numCompletedRequests++;
            if (toastId.current) toast.update(toastId.current, {progress: numCompletedRequests / numTotalRequests})
            // Call the next one in line
            if (curRequestIds.length) {
              let reqToSend = curRequestIds.find(req => !req.sent);
              if (reqToSend) {
                reqToSend.sent = true;
                $.ajax(reqToSend);
              }
            } else {
              if (toastId.current) toast.done(toastId.current)

            }

            addToPlotFunction(data, requestData);
          },
          error: (xhr, textStatus, err) => {
            if (!curRequestIds.find(req => req.requestId === requestId)) return;
            if (textStatus) {
              tryCount++;
              if (tryCount <= MAX_REQUEST_RETRIES) {
                setTimeout(() => $.ajax(ajaxReq), getRandomInt(1000, 5000) * tryCount)
                return;
              }
              curRequestIds = curRequestIds.filter(item => item.requestId !== requestId)
              toast.error("Request failed, please try again!");
              console.log(err);
            }
          }
        }
        curRequestIds.push(ajaxReq);
        // console.log(curRequestIds);
        // console.log(curRequestIds.filter(req => {
        //     console.log(req.sent);
        //     return req.sent
        // }));
        if (curRequestIds.filter(req => req.sent).length < MAX_CONCURRENT_REQUESTS) {
          ajaxReq.sent = true
          $.ajax(ajaxReq);
        }
        // }
        // }, 500)
      }

      /**
       * Called when new movement data is received.
       * @param data The incoming data to process
       */
      const processNewMovementData = (data, req) => {
        if (Object.keys(data).find(k => k === 'speed')) {
          toast.warn('No data found.');
          return;
        }
        for (let [journeyId, points] of Object.entries(data)) {
          if ((journeyId in movementData)) {
            movementData[journeyId] = [...movementData[journeyId], ...points]
            // for (let p of points) movementData[journeyId].push(p);
          } else movementData[journeyId] = points;
        }
        for (let journeyId of Object.keys(movementData)) {
          if (Array.isArray(movementData[journeyId])) {
            movementData[journeyId].sort((a, b) => a[3] - b[3])
          }
        }
        calculateTravelTimes(req.startDateTime, req.endDateTime);
        if (!curRequestIds.find(r => r.url.match('points'))) {
          addMovementDataToPlot();

          let [startDateTime, endDateTime] = getDateTimes([startDateLocal, endDateLocal], timeZoneId)

          wejoChartOptions = getChartOptions(startDateTime, endDateTime)
          updateChartOptions();

          chart.setOption(wejoChartOptions);
        }
      }

      /**
       * Called when new truck data is received.
       * @param data The incoming data to process
       */
      const processNewTruckData = (data, req) => {
        if (Object.keys(data).find(k => k === 'speed')) {
          toast.warn('No data found.');
          return;
        }

        Object.keys(data).forEach((deviceId) => {
          const newKey = `truck_${deviceId}`
          if (deviceId !== newKey) {
            Object.defineProperty(data, newKey,
              Object.getOwnPropertyDescriptor(data, deviceId));
            delete data[deviceId];
          }
        })

        for (let [deviceId, points] of Object.entries(data)) {
          if ((deviceId in movementData)) {
            movementData[deviceId] = [...movementData[deviceId], ...points]
            // for (let p of points) movementData[journeyId].push(p);
          } else movementData[deviceId] = points;
        }
        for (let deviceId of Object.keys(movementData)) {
          if (Array.isArray(movementData[deviceId])) {
            movementData[deviceId].sort((a, b) => a[3] - b[3])
          }
        }
        calculateTravelTimes(req.startDateTime, req.endDateTime);
        if (!curRequestIds.find(r => r.url.match('points'))) {
          addMovementDataToPlot();

          let [startDateTime, endDateTime] = getDateTimes([startDateLocal, endDateLocal], timeZoneId)

          wejoChartOptions = getChartOptions(startDateTime, endDateTime)
          updateChartOptions();

          chart.setOption(wejoChartOptions);
        }
      }

      /**
       * Called when new event data is received.
       * @param data The incoming data to process
       */
      const processNewEventData = data => {
        for (let event of data) {
          eventData.push(event);
        }
        addEventDataToPlot();
        chart.setOption({
          series: getAllSeries(),
          yAxis: wejoChartOptions.yAxis,
          xAxis: wejoChartOptions.xAxis,
        }, {
          lazyUpdate: true
        })
      }

      const processNewJourneys = (data, requestData) => {
        // prepend 'truck_' to each deviceId
        if (requestData.truckTable) {
          Object.keys(data).forEach((deviceId) => {
            const newKey = `truck_${deviceId}`
            if (deviceId !== newKey) {
              Object.defineProperty(data, newKey,
                Object.getOwnPropertyDescriptor(data, deviceId));
              delete data[deviceId];
            }
          })
        }
        // if (Object.keys(data).length >= 1) setDisableDownloadTrips(false);
        for (let [journeyId, journey] of Object.entries(data)) {
          travelTimeJourneys[journeyId] = journey;
          // for (let point of travelTimeSeriesList.data) {
          //     if (point[3] === journeyId) {
          //         point.itemStyle = {color: '#000'}
          //     }
          // }
        }
        if (chart && !curRequestIds.find(r => r.url.match('journeys'))) {
          chart.setOption({series: getAllSeries(false)})
          // toast.dismiss();
          toast.success("Trips loaded!");
        }
      }

      /**
       * Adds movement data to the plot.
       * First, this groups all the points in each trajectory into their speed categories.
       * Next, the r^2 value of all the points in each speed category is calculated.
       * If that r^2 value is more than 0.999 (near-perfectly straight), then only draw the start and end points of the line.
       *
       * This saves on RAM considerably.
       * It is likely that at some point this same algorithm will be implemented in the Python backend,
       * reducing the amount of data being sent over the network to begin with.
       */
      const addMovementDataToPlot = () => {
        let tempTrajectoryTotal = 0;
        let tempPointTotal = 0;

        let maxTime = 0;

        speedSeriesArray = createSpeedSeriesArray(FIRST_BUCKET_SIZE, BUCKET_SIZE, MAX_SPEED);

        for (let [journeyId, journey] of Object.entries(movementData)) {
          if (journey.length > 1) {
            tempTrajectoryTotal++;

            // if (!showTruckDataRef.current && journeyId.match(/^truck_/)) continue;

            // Calculate speed series indexes first
            for (let p of journey) {
              if (p.length === 7) continue;
              // Some speeds are negative for some reason
              p[2] = Math.abs(p[2])
              p[6] = getSpeedSeriesIndex(p[2]);
            }

            const CONNECT_THRESHOLD = journeyId.match('truck') ? Number.MAX_SAFE_INTEGER : 0.25;
            // Groups the journey by speed series index
            // https://stackoverflow.com/questions/34519633/group-sequential-repeated-values-in-javascript-array
            let groupedJourney = journey.reduce(function (r, i) {
              if ((typeof r.last === 'undefined' || r.last !== i[6]) ||
                (typeof r.lastDist === 'undefined' || Math.abs(r.lastDist - i[1]) >= CONNECT_THRESHOLD)) {
                r.last = i[6];
                r.lastDist = i[1];
                r.arr.push([]);
              }
              r.arr[r.arr.length - 1].push(i);
              return r;
            }, {arr: []}).arr;

            // Add the first value of the next group to each group, this way lines connect
            // Make sure the last point of the current chunk and the first point of the next chunk are close enough though.
            for (let i = 0; i < groupedJourney.length - 1; i++) {
              let curDist = groupedJourney[i][groupedJourney[i].length - 1][1];
              let nextDist = groupedJourney[i + 1][0][1];
              if (!(Math.abs(curDist - nextDist) > CONNECT_THRESHOLD)) {
                groupedJourney[i].push(groupedJourney[i + 1][0]);
              }
            }

            // Filter and add the points in
            for (let subJourney of groupedJourney) {
              // Calculate distance, time, and average speed for the chunk
              // Skip this for really short sub journeys
              if (subJourney.length > 2) {
                if (subJourney[0][3] > maxTime) maxTime = subJourney[0][3];

                let distance = subJourney[subJourney.length - 2][1] - subJourney[0][1];
                let time = subJourney[subJourney.length - 2][3] - subJourney[0][3];
                let averageSpeed = distance / (time / 60 / 60);

                // Min/max speed for that series
                let minSpeed = speedSeriesArray[subJourney[0][6]].minSpeed;
                let maxSpeed = speedSeriesArray[subJourney[0][6]].maxSpeed;

                // Make sure it doesn't go backward, with 100ft allowance
                if (distance < 0) continue;
                // Make sure it's +/- 10mph of the min/max speed
                if (averageSpeed > (maxSpeed + 10) || averageSpeed < (minSpeed - 10)) continue;
              }
              // Super short sub journeys that go really far are the cause of pink streaks, get rid of them
              else {
                let distance = subJourney[subJourney.length - 1][1] - subJourney[0][1];
                if (distance < 0 || distance > .25) continue
              }

              // Find the R^2 value
              if (subJourney.length > 2) {
                let startTime = subJourney[0][3]

                // Do linear regression to find the r^2 value
                let r2 = linearRegression(subJourney, startTime);
                // If it's super high, only draw the start and end points
                if (r2 > 0.999) {
                  tempPointTotal += subJourney.length;
                  speedSeriesArray[subJourney[0][6]].data.push({
                    coords: [subJourney[0], subJourney[subJourney.length - 1]]
                  });
                }
                // Otherwise, draw all the points
                else {
                  tempPointTotal += subJourney.length;
                  speedSeriesArray[subJourney[0][6]].data.push({
                    coords: subJourney
                  });
                }
              } else if (subJourney.length === 2) {
                tempPointTotal += subJourney.length;
                speedSeriesArray[subJourney[0][6]].data.push({
                  coords: subJourney
                });
              }
            }
          }
        }

        maxUnixTime.current = maxTime;

        // Show the plot and update fields
        chart.hideLoading()

        dispatch(setTrajectoryStore({
          totalTrajectories: tempTrajectoryTotal,
          totalPoints: tempPointTotal,
        }));
        // setTotalTrajectories(tempTrajectoryTotal);
        // setTotalPoints(tempPointTotal);
      }

      /**
       * Boilerplate linear regression algo
       * Modified and reduced version of this: https://gist.github.com/Eunoia/2fa8bcab3b88527e82cfc9df12763925
       * @param points The list of points
       * @param xOffset The amount that should be subtracted from each x value (likely the epoch time (s) of the first point.
       * @returns {number} Just the r^2 value, we don't care about the rest.
       */
      function linearRegression(points, xOffset) {
        var lr = {};
        var n = points.length - 1;
        var sum_x = 0;
        var sum_y = 0;
        var sum_xy = 0;
        var sum_xx = 0;
        var sum_yy = 0;

        for (var i = 0; i < points.length - 1; i++) {
          // Don't modify the original array... it creates wacky problems
          let adjusted_tstamp = points[i][3] - xOffset;
          sum_x += adjusted_tstamp;
          sum_y += points[i][1];
          sum_xy += (adjusted_tstamp * points[i][1]);
          sum_xx += (adjusted_tstamp * adjusted_tstamp);
          sum_yy += (points[i][1] * points[i][1]);
        }

        return Math.pow((n * sum_xy - sum_x * sum_y) / Math.sqrt((n * sum_xx - sum_x * sum_x) * (n * sum_yy - sum_y * sum_y)), 2);
      }

      /**
       * Add event data to the plot
       */
      const addEventDataToPlot = () => {
        /**
         * This is a wrapper of sorts for getCorrectedMileMarker. Prevents needing to keep a duplicate of all data in mem.
         * @param point The point to be corrected
         */
        let getCorrectedEventPoint = (point) => {
          let newPoint = JSON.parse(JSON.stringify(point));
          newPoint[1] = getCorrectedMileMarker(newPoint[1]);
          return newPoint;
        }

        // Reset the series so we don't get duplicates
        hardBrakingHistogramSeries = createAccelerationChangeHistogramSeries('BRAKE')
        hardAccelerationHistogramSeries = createAccelerationChangeHistogramSeries('ACCELERATION')
        hardBrakingSeries = createAccelerationChangeEventSeries('BRAKE');
        hardAccelerationSeries = createAccelerationChangeEventSeries('ACCELERATION');

        if (!eventData.length) return;

        for (let event of eventData) {
          // let tempEvent = getCorrectedEventPoint(event);
          // let tempEvent = event;
          // let rawEvent = [tempEvent[0], tempEvent[1]]
          if (event[2] === 'ACCELERATION_CHANGE') {
            if (event[3] === 'HARD_BRAKE') {
              hardBrakingSeries.data.push(event);
            } else if (event[3] === 'HARD_ACCELERATION') {
              hardAccelerationSeries.data.push(event);
            }
          }
        }

        // Figure out how wide histogram bins should be
        let histogramBinWidth;
        if (maxMileMarker > 0 && maxMileMarker <= 1) {
          histogramBinWidth = .01
        } else if (maxMileMarker > 1 && maxMileMarker <= 5) {
          histogramBinWidth = .05
        } else if (maxMileMarker > 5 && maxMileMarker <= 10) {
          histogramBinWidth = .1
        } else if (maxMileMarker > 10 && maxMileMarker <= 20) {
          histogramBinWidth = .2
        } else if (maxMileMarker > 20 && maxMileMarker <= 40) {
          histogramBinWidth = .5
        } else histogramBinWidth = 1

        // Group the event data into bins for the histogram
        let numCategories = Math.floor(maxMileMarker / histogramBinWidth)
        let categories = [];
        let hardBrakingBins = Array(numCategories).fill(0);
        let hardAccelerationBins = Array(numCategories).fill(0);
        for (let i = 0; i < numCategories; i++) {
          // Find the min and max for this bin
          let min = getCorrectedMileMarker(i * histogramBinWidth);
          let max = getCorrectedMileMarker((i + 1) * histogramBinWidth);
          // If we're subtracting, swap min and max
          if (subtractMileMarkersRef.current) {
            let tmp = min;
            min = max;
            max = tmp;
          }
          // Make the label
          categories[i] = (min.toFixed(2) + "-" + max.toFixed(2))

          // Find the hard braking points for this bin
          for (let e of hardBrakingSeries.data) {
            let mm = getCorrectedMileMarker(e[1]);
            if (mm > min && mm <= max) {
              hardBrakingBins[i]++;
            }
          }
          // Find the hard acceleration points for this bin
          for (let e of hardAccelerationSeries.data) {
            let mm = getCorrectedMileMarker(e[1]);
            if (mm > min && mm <= max) {
              hardAccelerationBins[i]++;
            }
          }
        }
        let summedBins = hardBrakingBins.map(function (num, idx) {
          return num + hardAccelerationBins[idx];
        });
        let maxBin = Math.max(...summedBins);
        // Scale the histogram x axis to the nearest 5
        if (maxBin >= 5) {
          wejoChartOptions.xAxis[0].max = Math.ceil((maxBin + 1) / 5) * 5;
        }

        // Set chart options for the histogram
        // wejoChartOptions.yAxis[0].data = (subtractMileMarkersStore) ? categories.reverse() : categories;
        // hardBrakingHistogramSeries.data = (subtractMileMarkersStore) ? bins.reverse() : bins;
        wejoChartOptions.yAxis[0].data = categories;
        hardBrakingHistogramSeries.data = hardBrakingBins;
        hardAccelerationHistogramSeries.data = hardAccelerationBins;

        // Update the count
        dispatch(setTrajectoryStore({
          // totalTrajectories: 0,
          // totalPoints: 0,
          totalHardBrakingEvents: hardBrakingSeries.data.length,
          totalHardAccelerationEvents: hardAccelerationSeries.data.length,
          // totalTravelTimePoints: 0
        }));
        // totalHardBrakingEvents: 0,
        //     totalHardAccelerationEvents: 0,
        //     totalTravelTimePoints: 0
        // setTotalHardBrakingEvents(hardBrakingSeries.data.length);
        // setTotalHardAccelerationEvents(hardAccelerationSeries.data.length);
      }

      /**
       * Calculates the travel times for incoming movement data
       * This assumes the data is already sorted by time
       * @param startTime Start time to re-query for
       * @param endTime End time to re-query for
       */
      const calculateTravelTimes = (startTime, endTime) => {
        let newTravelTimeData = [];

        let onlyTimes = [];
        // The epoch start/end times for this query
        let travelTimeStartTime = 0;
        let travelTimeEndTime = Number.MAX_SAFE_INTEGER;

        let travelTimeSegments = [];

        for (let [journeyId, journey] of Object.entries(movementData)) {
          const maxDistanceFromEndsForJourney = journeyId.match(/^truck_/) ? 1.5 : maxDistanceFromEndsRef.current;

          if (journey.length <= 1) continue;
          // Check if the journey went the whole way
          if (journey[0][1] < maxDistanceFromEndsForJourney &&
            journey[journey.length - 1][1] > (maxMileMarker - maxDistanceFromEndsForJourney)) {
            let travelTime = journey[journey.length - 1][3] - journey[0][3];
            onlyTimes.push(travelTime);

            newTravelTimeData.push([journey[0][0], travelTime, journey[0][3], journeyId])
            travelTimeStartTime = (travelTimeStartTime > journey[0][0]) ? journey[0][0] : travelTimeStartTime;
            travelTimeEndTime = (travelTimeEndTime < journey[0][0]) ? journey[0][0] : travelTimeEndTime;
            travelTimeSegments.push({
              journeyId,
              startTime: journey[0][3],
              endTime: journey[journey.length - 1][3]
            });
          }
        }

        dispatch(setTrajectoryStore({travelTimes: onlyTimes}))

        // This prevents sending duplicate requests
        if (newTravelTimeData.length === travelTimeData.length) return;
        else travelTimeData = newTravelTimeData;

        let maxDuration = dayjs.duration(Math.max(...onlyTimes) * 1000);
        for (let travelTime of travelTimeData) {
          let duration = dayjs.duration(travelTime[1] * 1000);
          travelTime[1] = (duration.hours() * 60) + (duration.minutes()) + (duration.seconds() / 60);
        }

        if (travelTimeSeries && travelTimeData.length >= 1) {
          // travelTimeData = filterOutliers(travelTimeData)

          onlyTimes = [];
          for (let travelTime of travelTimeData) {
            onlyTimes.push(travelTime[1]);
          }

          let max = Math.max(...onlyTimes)
          // if (!loadedMaxTravelTime) {
          //     setMaxTravelTime(Math.max(...onlyTimes));
          // }
          onlyTravelTimes.current = onlyTravelTimes.current.concat(onlyTimes);
          // setOnlyTravelTimes(onlyTravelTimes.concat(onlyTimes));

          let travelTimeSeriesData = [];
          for (const travelTime of travelTimeData) {
            travelTimeSeriesData.push({
              value: [travelTime[0], travelTime[1]],
              name: travelTime[3]
            })
          }
          travelTimeSeries.data = travelTimeSeriesData;
          // wejoChartOptions = getChartOptions(startDateTime, endDateTime);
          dispatch(setTrajectoryStore({
            // totalTrajectories: 0,
            // totalPoints: 0,
            // totalHardBrakingEvents: 0,
            // totalHardAccelerationEvents: 0,
            totalTravelTimePoints: travelTimeData.length
          }));
          // setTotalTravelTimePoints(travelTimeData.length);
          calculateTravelTimeMA();

          // Create request to get the entire journeys for all the full journeys -- NO TRUCKS
          createRequestForWejoData({
              route: equidistantGeoJSON,
              startDateTime: startTime,
              endDateTime: endTime,
              journeySegments: travelTimeSegments.filter((segment) => !segment.journeyId.match(/^truck_/)),
              movementTable: movementTableStore,
            }, 'get_wejo_journeys', processNewJourneys
          );

          if (travelTimeSegments.filter((segment) => segment.journeyId.match(/^truck_/)).length) {

            // Create request to get the entire journeys for all the full journeys -- ONLY TRUCKS
            createRequestForWejoData({
                route: equidistantGeoJSON,
                startDateTime: startTime,
                endDateTime: endTime,
                journeySegments: travelTimeSegments
                  .filter((segment) => segment.journeyId.match(/^truck_/))
                  // rename journeyId to deviceId and get rid of the 'truck_' prefix
                  .map((segment) => ({
                      deviceId: segment.journeyId.slice(6),
                      startTime: segment.startTime,
                      endTime: segment.endTime
                    })
                  ),
                truckTable: truckTableStore,
              }, 'get_wejo_truck_journeys', processNewJourneys
            );
          }
        }
      }

      const calculateTravelTimeMA = () => {
        travelTimeMAData = [];
        travelTimeData = travelTimeData.sort((a, b) => {
          return a[2] - b[2];
        })
        if (travelTimeData.length > 10) {
          for (let i = 4; i < travelTimeData.length - 5; i++) {
            let subset = travelTimeData.slice(i - 5, i + 5);
            let avg = subset.reduce((acc, val) => acc + val[1], 0) / subset.length;
            travelTimeMAData.push([travelTimeData[i][0], avg])
          }
          travelTimeMASeries.data = travelTimeMAData;
        }
      }

      function filterOutliers(someArray) {
        // Copy the values, rather than operating on references to existing values
        var values = someArray.concat();

        // Then sort
        values.sort(function (a, b) {
          return a[1] - b[1];
        });

        /* Then find a generous IQR. This is generous because if (values.length / 4)
         * is not an int, then really you should average the two elements on either
         * side to find q1.
         */
        var q1 = values[Math.floor((values.length / 4))][1];
        // Likewise for q3.
        var q3 = values[Math.ceil((values.length * (3 / 4)))][1];
        var iqr = q3 - q1;

        // Then find min and max values
        var maxValue = q3 + iqr * 1.5;
        var minValue = q1 - iqr * 1.5;

        // Then filter anything beyond or beneath these values.
        var filteredValues = values.filter(function (x) {
          return (x[1] <= maxValue) && (x[1] >= minValue);
        });

        // Then return
        return filteredValues;
      }

// const standardDeviation = (arr, usePopulation = false) => {
//     const mean = arr.reduce((acc, val) => acc + val, 0) / arr.length;
//     return Math.sqrt(
//         arr
//             .reduce((acc, val) => acc.concat((val - mean) ** 2), [])
//             .reduce((acc, val) => acc + val, 0) /
//         (arr.length - (usePopulation ? 0 : 1))
//     );
// };

      const getCorrectedMileMarker = mileMarker => {
        if (subtractMileMarkersRef.current) {
          return mileMarkerOffsetRef.current - mileMarker;
        } else return mileMarkerOffsetRef.current + mileMarker;
      }

      const getRoundedMileMarker = mileMarker => {
        return Math.round(getCorrectedMileMarker(mileMarker) * 100) / 100;
      }

      const downloadTrips = () => {
        let output = [];
        const NUM_INTERMEDIATE_POINTS = 10;
        // For each trip in the travel time plot
        for (let [journeyId, line] of Object.entries(travelTimeJourneys)) {
          // Make sure it has corresponding movement data (the stuff that shows up in the main plot)
          if (journeyId in movementData) {
            // Decode the polyline
            let coordinates = polyline.decode(line);
            if (coordinates.length > 2) {
              // Flip the coords to lon, lat
              for (let coordinate of coordinates) coordinate.reverse();
              let linestring = turf.lineString(coordinates);
              // The output object
              let tripOutput = {};
              // tripOutput[`startTime ${timeZoneName}`] = dayjs(movementData[journeyId][0][0]).tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
              // tripOutput[`endTime ${timeZoneName}`] = dayjs(movementData[journeyId][movementData[journeyId].length - 1][0]).tz(timeZoneId).format('YYYY-MM-DD HH:mm:ss')
              tripOutput.startTime = dayjs(movementData[journeyId][0][0]).utc().format('YYYY-MM-DD HH:mm:ss')
              tripOutput.endTime = dayjs(movementData[journeyId][movementData[journeyId].length - 1][0]).utc().format('YYYY-MM-DD HH:mm:ss')
              tripOutput.distanceTraveled = length(linestring, {units: 'miles'})
              // tripOutput.startLon = coordinates[0][0]
              // tripOutput.startLat = coordinates[0][1]
              // tripOutput.endLon = coordinates[coordinates.length - 1][0]
              // tripOutput.endLat = coordinates[coordinates.length - 1][1]
              tripOutput.encodedPolyline = line;
              tripOutput.journeyId = journeyId;
              // Keep an array of the coordinates
              // let points = [coordinates[coordinates.length - 1]];
              // for (let i = NUM_INTERMEDIATE_POINTS; i > 0; i--) {
              //     // The size of the gaps between selected points (for 100 coords, this would be 10)
              //     let chunkSize = (coordinates.length - 1) / NUM_INTERMEDIATE_POINTS - 1;
              //     // The index to look at
              //     let idx = Math.ceil((i * chunkSize) + chunkSize);
              //     // Validate the index
              //     if (idx <= coordinates.length - 1) {
              //         // Push the point
              //         if (coordinates[idx]) points.push(coordinates[idx])
              //         // Add the point's lon, lat
              //
              //         if (coordinates[idx] && coordinates[idx].length === 2) {
              //             tripOutput[`point${i + 1}Lon`] = coordinates[idx][0];
              //             tripOutput[`point${i + 1}Lat`] = coordinates[idx][1];
              //         } else {
              //             tripOutput[`point${i + 1}Lon`] = null;
              //             tripOutput[`point${i + 1}Lat`] = null;
              //         }
              //     }
              // }
              // Add the end point
              // points.push(coordinates[0])
              // Convert to geoJson
              // let geoJsonLine = turf.lineString(points);
              // Convert to WKT
              // tripOutput.linestring = wellknown.stringify(geoJsonLine);
              // Push
              output.push(tripOutput)
            }
          }
        }
        let outputStr = Papa.unparse(output);
        let fileName = '';
        if (routeNameTemp) fileName = `trajectory_heatmap_trips_${routeNameTemp.replace(/\s/g, '_')}_${startDateLocal}_${endDateLocal}.csv`;
        else fileName = `trajectory_heatmap_trips_${startDateLocal}_${endDateLocal}.csv`;
        download(fileName, outputStr);
      }

      function getRandomInt(min, max) {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min) + min); //The maximum is exclusive and the minimum is inclusive
      }

      function round(date, duration, method) {
        return dayjs(Math[method]((+date) / (+duration)) * (+duration)).tz(timeZoneId);
      }

      /**
       * Start and end are YYYY/MM/DD
       * @param start
       * @param end
       * @param timezone
       * @param asString
       */
      const getDateTimes = ([start, end], timezone, asString = true) => {
        let startDateTime = dayjs.tz(`${start} 00:00:00`, timezone)
        let endDateTime = dayjs.tz(`${end} 23:59:59`, timezone)
        if (asString) {
          return [startDateTime, endDateTime].map(d => d.toISOString());
        } else return [startDateTime, endDateTime];
      }

      return (
        <div id="chart-container">
          <Space direction="vertical" size="large" style={{width: '100%'}}>
            <div id="trajectory-chart" style={{
              width: '100%',
              // height: 'calc(100vh - 2.5em)',
              height: 'calc(100vh - 100px)',
              fontSize: 36,
              textAlign: 'center',
              paddingTop: '300px'
            }}>Select a date range and a route!
            </div>
          </Space>
          <ToastContainer position="bottom-right" autoClose={2000} closeOnClick={true} pauseOnHover={true}
                          pauseOnFocusLoss={false}/>
          <StatisticsModal/>
        </div>
      )
    }
  )
;

export default TrajectoryChart;
